import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import MomentCard from '@/components/base/MomentCard';
import { useSeo } from '@/hooks/useSeo';
import { momentCategories, moments } from '@/mocks/moments';
import { siteInfo } from '@/mocks/site';

const sortOptions = [
  { key: 'latest', label: '最新收录' },
  { key: 'hot', label: '最多观看' },
];

export default function Moments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const category = searchParams.get('category') ?? '全部';
  const sort = searchParams.get('sort') ?? 'latest';

  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const updateParams = (next: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (!value || value === '全部') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  const list = useMemo(() => {
    const lower = q.trim().toLowerCase();
    const filtered = moments.filter((item) => {
      const categoryOk = category === '全部' || item.category === category;
      const text = `${item.title} ${item.summary} ${item.tags.join(' ')} ${item.category}`.toLowerCase();
      const keywordOk = !lower || text.includes(lower);
      return categoryOk && keywordOk;
    });

    return [...filtered].sort((a, b) => {
      if (sort === 'hot') {
        return b.views - a.views;
      }
      return a.date < b.date ? 1 : -1;
    });
  }, [q, category, sort]);

  const seoTitle = q
    ? `${q} 相关名场面整理｜${siteInfo.name}`
    : '名场面图鉴｜私拍吃瓜抓拍·神操作·高光瞬间全集';

  useSeo({
    title: seoTitle,
    description: `按主题分类浏览私拍吃瓜名场面与抓拍整理,支持关键词搜索与观看量排序,快速定位神操作、尴尬瞬间与高光瞬间。共收录 ${moments.length} 条内容。`,
    keywords: '私拍吃瓜名场面,私拍吃瓜抓拍,名场面图鉴,神操作,高光瞬间',
    path: '/moments',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '名场面图鉴',
        url: `${siteInfo.domain}/moments`,
        description: '私拍吃瓜名场面与抓拍整理集合页。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.domain },
          { '@type': 'ListItem', position: 2, name: '名场面图鉴', item: `${siteInfo.domain}/moments` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: list.slice(0, 12).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `${siteInfo.domain}/moment/${item.id}`,
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="px-4 md:px-8 pt-8 md:pt-10 pb-8 bg-background-100">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '名场面图鉴' }]} />

          <div className="mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950">名场面图鉴</h1>
              <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">
                这里收录了全部私拍吃瓜名场面与抓拍整理。用分类缩小范围、用关键词直接找画面,每条都标注了最值得看的关键节点。
              </p>
            </div>

            <form onSubmit={handleSearch} className="flex w-full lg:w-auto gap-3">
              <div className="flex flex-1 lg:w-80 items-center gap-2 px-4 py-3 rounded-md border border-background-300 bg-background-50">
                <i className="ri-search-line text-lg text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索名场面关键词"
                  aria-label="搜索名场面关键词"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-3 rounded-md text-sm whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="mt-7 flex flex-wrap gap-2">
            {momentCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => updateParams({ category: item })}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  category === item
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-50 text-foreground-700 hover:text-foreground-950 border border-background-200'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 py-10 md:py-14">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-7">
            <p className="text-sm text-foreground-600">
              共 <span className="text-primary-700">{list.length}</span> 条结果
              {q && <span className="text-foreground-500"> · 关键词「{q}」</span>}
            </p>
            <div className="inline-flex items-center gap-1 rounded-full bg-background-200/70 p-1 self-start">
              {sortOptions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => updateParams({ sort: item.key })}
                  className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    sort === item.key
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {list.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {list.map((item) => (
                <MomentCard key={item.id} moment={item} />
              ))}
            </div>
          ) : (
            <div className="py-20 rounded-lg border border-dashed border-background-300 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl"></i>
              </div>
              <p className="mt-4 text-sm text-foreground-700">没有找到匹配的名场面。</p>
              <p className="mt-2 text-xs text-foreground-500">试试更短的关键词,或者切回「全部」分类。</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setSearchParams(new URLSearchParams());
                }}
                className="mt-5 px-5 py-2.5 rounded-md text-sm whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}